import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AppShell } from "../components/Layout";
import { api, formatINR, blurOnWheel } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { CreditCard, Clock, Upload, XCircle, AlertTriangle } from "lucide-react";

function todayBatch(type) {
  const d = new Date();
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}_${type}`;
}

// Days since approval (ISO string). Returns integer >=0.
const daysSinceApproval = (approvalIso) => {
  if (!approvalIso) return 0;
  const then = new Date(approvalIso).getTime();
  if (isNaN(then)) return 0;
  const diffMs = Date.now() - then;
  return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
};

// Aging colour bucket per user spec: green <2d, yellow 2–5d, red >5d.
const agingBadge = (days) => {
  if (days < 2) return { cls: "bg-emerald-100 text-emerald-700 border-emerald-300", label: `${days}D` };
  if (days <= 5) return { cls: "bg-amber-100 text-amber-700 border-amber-400", label: `${days}D` };
  return { cls: "bg-red-100 text-red-700 border-red-400", label: `${days}D` };
};

// Default the "Payment Date" input to today (yyyy-mm-dd local) for the modal.
const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export default function PaymentQueue() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("12PM");
  const [payTarget, setPayTarget] = useState(null);  // request being paid
  const [failTarget, setFailTarget] = useState(null); // request being marked failed
  const [payStatus, setPayStatus] = useState(null);
  const navigate = useNavigate();

  // Pay form state — recreated each time the modal opens.
  const [form, setForm] = useState({
    amount: "", mode: "Vendor Account", date: todayLocal(),
    ref: "", notes: "", employee_name: "", file: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const [failReason, setFailReason] = useState("");
  const [failSubmitting, setFailSubmitting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Phase 2: queue includes both approved AND partially_paid items.
      const r = await api.get("/requests?status=approved,partially_paid&limit=500");
      setItems(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => {
    fetchData();
    api.get("/payment-status").then((r) => setPayStatus(r.data)).catch(() => {});
  }, []);

  const batches = useMemo(() => {
    const out = { "12PM": {}, "6PM": {} };
    const b12 = todayBatch("12PM");
    const b6 = todayBatch("6PM");
    items.forEach((r) => {
      let key = null;
      if (r.payment_batch === b12) key = "12PM";
      else if (r.payment_batch === b6) key = "6PM";
      else if (r.payment_batch?.endsWith("_12PM")) key = "12PM";
      else if (r.payment_batch?.endsWith("_6PM")) key = "6PM";
      if (!key) return;
      const v = r.vendor_name;
      if (!out[key][v]) out[key][v] = { vendor: v, vendor_id: r.vendor_id, requests: [], total: 0 };
      out[key][v].requests.push(r);
      // Residual balance drives the vendor total now.
      const remaining = (r.remaining_amount != null)
        ? r.remaining_amount
        : (r.approved_amount || 0) - (r.total_paid || 0);
      out[key][v].total += remaining;
    });
    return out;
  }, [items]);

  const openPay = (r) => {
    const remaining = (r.remaining_amount != null)
      ? r.remaining_amount
      : (r.approved_amount || 0) - (r.total_paid || 0);
    setForm({
      amount: String(remaining || ""),
      mode: r.requested_payment_mode === "Bank"
        ? "Vendor Account"
        : (r.requested_payment_mode === "UPI" || r.requested_payment_mode === "Bank Transfer")
          ? "Vendor Account"
          : (r.requested_payment_mode || "Vendor Account"),
      date: todayLocal(),
      ref: "",
      notes: "",
      employee_name: r.employee_name || "",
      file: null,
    });
    setPayTarget(r);
  };

  const submitPay = async () => {
    if (!payTarget) return;
    const amt = parseFloat(form.amount);
    const remaining = (payTarget.remaining_amount != null)
      ? payTarget.remaining_amount
      : (payTarget.approved_amount || 0) - (payTarget.total_paid || 0);

    if (!amt || amt <= 0) return toast.error("Amount must be > 0");
    if (amt > remaining + 0.01) return toast.error(`Amount exceeds remaining ₹${remaining}`);
    if (!form.ref?.trim()) return toast.error("Reference / Transaction ID is required");
    // v2.12 — proof file is now OPTIONAL
    if (!["Cash", "Vendor Account", "Employee Account"].includes(form.mode)) return toast.error("Select a payment mode");
    if (form.mode === "Employee Account" && !form.employee_name?.trim()) return toast.error("Employee name is required for Employee Account");

    const fd = new FormData();
    fd.append("payment_mode", form.mode);
    fd.append("paid_amount", String(amt));
    fd.append("payment_date", new Date(form.date).toISOString());
    fd.append("reference_id", form.ref.trim());
    if (form.notes?.trim()) fd.append("notes", form.notes.trim());
    if (form.mode === "Employee Account") fd.append("employee_name", form.employee_name.trim());
    if (form.file) fd.append("proof_file", form.file);

    setSubmitting(true);
    try {
      let res;
      try {
        res = await api.patch(`/requests/${payTarget.request_id}/pay`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        });
      } catch (err) {
        // Phase C3 — payment_date beyond back-date window; admin can override with reason.
        const detail = err?.response?.data?.detail;
        if (detail && typeof detail === "object" && detail.code === "backdate_beyond_window" && user?.role === "admin") {
          const reason = window.prompt(
            `Payment date is ${detail.days_beyond_window} days beyond the back-date window.\n\nAdmin override reason (required — audit-logged):`
          );
          if (!reason || !reason.trim()) throw err;
          fd.append("admin_override", "true");
          fd.append("override_reason", reason.trim());
          res = await api.patch(`/requests/${payTarget.request_id}/pay`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 60000,
          });
        } else {
          throw err;
        }
      }
      if (res.data?.status === "recorded") toast.success("Payment recorded — fully paid");
      else toast.success("Partial payment recorded · queue updated");
      setPayTarget(null);
      fetchData();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error((detail && typeof detail === "object" ? detail.message : detail) || "Payment failed");
    } finally {
      setSubmitting(false);
    }
  };

  const submitFail = async () => {
    if (!failTarget) return;
    if (!failReason.trim()) return toast.error("Reason is mandatory");
    setFailSubmitting(true);
    try {
      await api.patch(`/requests/${failTarget.request_id}/mark-failed`, { reason: failReason.trim() });
      toast.success("Payment marked as failed");
      setFailTarget(null);
      setFailReason("");
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setFailSubmitting(false);
    }
  };

  const renderBatch = (key) => {
    const groups = Object.values(batches[key] || {});
    if (loading) return <div className="p-5 label-micro">LOADING…</div>;
    if (groups.length === 0) return (
      <div className="p-10 text-center font-mono text-xs uppercase tracking-widest text-slate-400">
        NO REQUESTS IN THIS BATCH
      </div>
    );
    return (
      <div className="space-y-0">
        {groups.map((g) => (
          <div key={g.vendor} className="bg-white border-b border-slate-200/10">
            <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
              <div>
                <div className="font-semibold text-base uppercase tracking-tight">{g.vendor}</div>
                <div className="label-micro">{g.requests.length} REQUEST{g.requests.length > 1 ? "S" : ""} DUE</div>
              </div>
              <div className="font-semibold text-2xl tracking-tight">{formatINR(g.total)}</div>
            </div>
            {g.requests.map((r) => {
              const remaining = (r.remaining_amount != null)
                ? r.remaining_amount
                : (r.approved_amount || 0) - (r.total_paid || 0);
              const isPartial = (r.status === "partially_paid") || (r.total_paid && r.total_paid > 0);
              const days = daysSinceApproval(r.approval_time);
              const badge = agingBadge(days);
              return (
                <div key={r.request_id} className="px-5 py-3 border-b border-slate-200 last:border-b-0 flex justify-between items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        data-testid={`req-link-${r.request_id}`}
                        onClick={() => navigate(`/requests/${r.request_id}`)}
                        className="font-mono text-xs font-bold hover:underline text-left"
                      >
                        {r.request_id}
                      </button>
                      <span
                        data-testid={`aging-${r.request_id}`}
                        className={`border ${badge.cls} font-mono text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5`}
                        title={`Approved ${days} day${days === 1 ? "" : "s"} ago`}
                      >
                        {badge.label}
                      </span>
                      {isPartial && (
                        <span className="bg-amber-100 text-amber-700 border border-amber-400 font-mono text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5">
                          PARTIAL
                        </span>
                      )}
                    </div>
                    <div className="label-micro mt-0.5">{r.site} · {r.category}</div>
                  </div>
                  <div className="text-right mr-2">
                    <div className="font-semibold">{formatINR(remaining)}</div>
                    {isPartial && (
                      <div className="font-mono text-[9px] text-slate-500">
                        of {formatINR(r.approved_amount)}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <button
                      data-testid={`pay-${r.request_id}`}
                      onClick={() => openPay(r)}
                      className="bg-black text-white px-3 py-2 font-mono text-[10px] uppercase tracking-widest font-bold hover:bg-olive transition-colors flex items-center gap-1"
                    >
                      <CreditCard size={12}/> PAY
                    </button>
                    <button
                      data-testid={`fail-${r.request_id}`}
                      onClick={() => { setFailTarget(r); setFailReason(""); }}
                      className="border border-red-500 text-red-600 px-3 py-1 font-mono text-[9px] uppercase tracking-widest font-bold hover:bg-red-500 hover:text-white transition-colors flex items-center justify-center gap-1"
                    >
                      <XCircle size={10}/> MARK FAILED
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  const remainingForTarget = payTarget
    ? ((payTarget.remaining_amount != null)
        ? payTarget.remaining_amount
        : (payTarget.approved_amount || 0) - (payTarget.total_paid || 0))
    : 0;

  return (
    <AppShell back="/dashboard" title="PAYMENT QUEUE">
      <div className="px-5 py-5">
        <h2 className="font-semibold text-2xl tracking-tight uppercase mb-4">
          {items.length} <span className="text-slate-400">DUE</span>
        </h2>

        {payStatus && (
          <div data-testid="payment-window-banner" className={`mb-4 border-2 p-3 flex items-center gap-2 ${
            payStatus.in_window ? "border-olive bg-olive/5" : "border-amber-500 bg-amber-50"
          }`}>
            <Clock size={14} className={payStatus.in_window ? "text-olive" : "text-amber-600"} />
            <div className="flex-1">
              <div className="font-mono text-[10px] uppercase tracking-widest font-bold">
                {payStatus.in_window ? "PAYMENT WINDOW OPEN" : "OUTSIDE PAYMENT WINDOW"}
              </div>
              <div className="font-mono text-[10px] text-slate-500 mt-0.5">
                Windows: {(payStatus.windows || []).map((w) => `${w.start}–${w.end}`).join(" · ")} IST
                {!payStatus.in_window && " · Only admin or emergency override."}
              </div>
            </div>
          </div>
        )}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className=" w-full grid grid-cols-2 bg-white border border-slate-200 p-0 h-auto">
            <TabsTrigger
              data-testid="tab-12pm"
              value="12PM"
              className=" font-mono text-xs uppercase tracking-widest font-bold py-3 data-[state=active]:bg-black data-[state=active]:text-white"
            >
              12 PM BATCH
            </TabsTrigger>
            <TabsTrigger
              data-testid="tab-6pm"
              value="6PM"
              className=" font-mono text-xs uppercase tracking-widest font-bold py-3 data-[state=active]:bg-black data-[state=active]:text-white"
            >
              6 PM BATCH
            </TabsTrigger>
          </TabsList>
          <TabsContent value="12PM" className="mt-4">{renderBatch("12PM")}</TabsContent>
          <TabsContent value="6PM" className="mt-4">{renderBatch("6PM")}</TabsContent>
        </Tabs>
      </div>

      {/* ===== PAYMENT EXECUTION MODAL — Phase 2 ===== */}
      <Dialog open={!!payTarget} onOpenChange={(o) => !o && !submitting && setPayTarget(null)}>
        <DialogContent
          data-testid="pay-modal"
          className="max-w-md  border border-slate-200 p-0 gap-0 max-h-[92vh] overflow-y-auto"
        >
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-200/10">
            <DialogTitle className="font-semibold uppercase tracking-tight">
              Execute Payment
            </DialogTitle>
            <DialogDescription className="font-mono text-[11px] text-slate-500">
              {payTarget && (
                <>
                  <span className="font-bold text-black">{payTarget.request_id}</span> · {payTarget.vendor_name}
                  <br/>
                  Remaining: <span className="font-bold text-black">{formatINR(remainingForTarget)}</span>
                  {payTarget.total_paid > 0 && (
                    <> · Already paid: <span className="font-bold text-black">{formatINR(payTarget.total_paid)}</span></>
                  )}
                  {payTarget.requested_payment_mode && (
                    <> · Requester preferred: <span className="font-bold text-black">{payTarget.requested_payment_mode}</span></>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="px-5 py-4 space-y-4">
            {/* Actual amount */}
            <div>
              <label className="label-micro block mb-1">ACTUAL AMOUNT PAID (₹)</label>
              <input
                data-testid="pay-amount"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                max={remainingForTarget}
                onWheel={blurOnWheel}
                onKeyDown={(e) => { if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault(); }}
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="w-full border border-slate-200 px-3 py-2 font-mono text-sm font-bold  focus:outline-none focus:ring-2 focus:ring-olive"
                placeholder="0"
              />
              <div className="font-mono text-[10px] text-slate-500 mt-1">
                Enter less than {formatINR(remainingForTarget)} to record a partial payment.
              </div>
            </div>

            {/* Actual mode */}
            <div>
              <label className="label-micro block mb-1">ACTUAL PAYMENT MODE</label>
              <div className="grid grid-cols-3 gap-0 border-t border-l border-slate-300">
                {["Cash", "Vendor Account", "Employee Account"].map((m) => (
                  <button
                    key={m}
                    type="button"
                    data-testid={`pay-mode-${m.toLowerCase().replace(/ /g, "-")}`}
                    onClick={() => setForm({ ...form, mode: m })}
                    className={`border-r border-b border-slate-300 py-3 font-mono text-[11px] uppercase tracking-widest font-bold transition-colors ${
                      form.mode === m ? "bg-black text-white" : "bg-white hover:bg-slate-100"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>

            {/* Employee name (only when Employee Account selected) */}
            {form.mode === "Employee Account" && (
              <div>
                <label className="label-micro block mb-1">EMPLOYEE NAME *</label>
                <input
                  data-testid="pay-employee-name"
                  type="text"
                  value={form.employee_name}
                  onChange={(e) => setForm({ ...form, employee_name: e.target.value })}
                  className="w-full border border-slate-200 px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-olive"
                  placeholder="Full name of employee receiving payment"
                />
              </div>
            )}

            {/* Date */}
            <div>
              <label className="label-micro block mb-1">PAYMENT DATE</label>
              <input
                data-testid="pay-date"
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                max={new Date().toISOString().slice(0, 10)}
                className="w-full border border-slate-200 px-3 py-2 font-mono text-sm  focus:outline-none focus:ring-2 focus:ring-olive"
              />
            </div>

            {/* Reference */}
            <div>
              <label className="label-micro block mb-1">REFERENCE / TRANSACTION ID *</label>
              <input
                data-testid="pay-ref"
                type="text"
                value={form.ref}
                onChange={(e) => setForm({ ...form, ref: e.target.value })}
                className="w-full border border-slate-200 px-3 py-2 font-mono text-sm  focus:outline-none focus:ring-2 focus:ring-olive"
                placeholder="UTR / UPI Ref / Cheque No"
              />
            </div>

            {/* Notes (optional) */}
            <div>
              <label className="label-micro block mb-1">NOTES (OPTIONAL)</label>
              <input
                data-testid="pay-notes"
                type="text"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full border border-slate-200/30 px-3 py-2 font-mono text-sm  focus:outline-none focus:ring-2 focus:ring-olive"
                placeholder="Optional note"
              />
            </div>

            {/* Proof file (optional) */}
            <div>
              <label className="label-micro block mb-1">PAYMENT PROOF (OPTIONAL · PDF / JPG / PNG)</label>
              <input
                ref={fileInputRef}
                data-testid="pay-proof-file"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*"
                onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                data-testid="pay-proof-pick"
                className="w-full border-2 border-dashed border-slate-300 px-3 py-3 font-mono text-xs uppercase tracking-widest font-bold flex items-center justify-center gap-2 hover:bg-olive/5 hover:border-olive transition-colors"
              >
                <Upload size={14}/>
                {form.file ? form.file.name : "Choose file (optional)"}
              </button>
              <div className="font-mono text-[10px] text-slate-500 mt-1">
                {form.file ? `Will upload to Google Drive · ${(form.file.size / 1024).toFixed(0)} KB` : "Reference / Transaction ID is sufficient · file attachment optional"}
              </div>
            </div>
          </div>

          <DialogFooter className="px-5 pb-5 pt-2 flex gap-2 sm:gap-2">
            <button
              data-testid="pay-cancel"
              onClick={() => !submitting && setPayTarget(null)}
              className="flex-1 border border-slate-200 px-4 py-3 font-mono text-xs uppercase tracking-widest font-bold bg-white hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              data-testid="pay-submit"
              onClick={submitPay}
              disabled={submitting}
              className="flex-1 bg-black text-white px-4 py-3 font-mono text-xs uppercase tracking-widest font-bold hover:bg-olive disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <CreditCard size={14}/>
              {submitting ? "RECORDING…" : "RECORD PAYMENT"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== MARK FAILED MODAL ===== */}
      <Dialog open={!!failTarget} onOpenChange={(o) => !o && !failSubmitting && setFailTarget(null)}>
        <DialogContent
          data-testid="fail-modal"
          className="max-w-md  border-2 border-red-500 p-0 gap-0"
        >
          <DialogHeader className="px-5 pt-5 pb-3 border-b-2 border-red-500/20 bg-red-50">
            <DialogTitle className="font-semibold uppercase tracking-tight text-red-700 flex items-center gap-2">
              <AlertTriangle size={18}/> Mark Payment Failed
            </DialogTitle>
            <DialogDescription className="font-mono text-[11px] text-slate-600">
              {failTarget && (
                <>
                  <span className="font-bold text-black">{failTarget.request_id}</span> · {failTarget.vendor_name}
                  <br/>This is terminal — the request cannot be paid afterwards.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="px-5 py-4">
            <label className="label-micro block mb-1">REASON *</label>
            <textarea
              data-testid="fail-reason"
              value={failReason}
              onChange={(e) => setFailReason(e.target.value)}
              rows={3}
              className="w-full border border-slate-200 px-3 py-2 font-mono text-sm  focus:outline-none focus:ring-2 focus:ring-red-500"
              placeholder="e.g. NEFT bounced, vendor account closed"
            />
          </div>
          <DialogFooter className="px-5 pb-5 pt-2 flex gap-2 sm:gap-2">
            <button
              data-testid="fail-cancel"
              onClick={() => !failSubmitting && setFailTarget(null)}
              className="flex-1 border border-slate-200 px-4 py-3 font-mono text-xs uppercase tracking-widest font-bold bg-white hover:bg-slate-100"
            >
              Cancel
            </button>
            <button
              data-testid="fail-submit"
              onClick={submitFail}
              disabled={failSubmitting}
              className="flex-1 bg-red-600 text-white px-4 py-3 font-mono text-xs uppercase tracking-widest font-bold hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {failSubmitting ? "MARKING…" : "MARK FAILED"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
